import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Autocomplete,
  Box,
  Card,
  Checkbox,
  Chip,
  Fade,
  Icon,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  Select,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import Loading from "components/Carteles/Loading";
import { MessageManager } from "context";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "95%", sm: "85%", lg: "75%" },
  overflowY: "auto",
};

const GET_COMANDERAS = gql`
  query getComanderas($order: Order) {
    comanderas(order: $order) {
      id
      printName
      estadoConexion
      habilitado
    }
  }
`;

const SAVE_COMANDERA = gql`
  mutation saveComandera($input: ComanderaInput!) {
    saveComandera(input: $input) {
      id
      printName
      estadoConexion
      habilitado
    }
  }
`;

const GET_CATEGORIAS_PRODUCTO = gql`
  query getCategoriasProductoAsignaciones {
    categoriasProducto(order: { field: "categoria", order: ASC }) {
      id
      categoria
    }
  }
`;

const GET_PRODUCTOS_ASIGNACIONES = gql`
  query getProductosAsignacionesComanderas {
    productos(order: { field: "nombre", order: ASC }) {
      id
      nombre
      categoriaProducto {
        id
      }
      comanderas {
        id
        printName
        habilitado
        estadoConexion
      }
    }
  }
`;

const SAVE_ASIGNACIONES_COMANDERAS = gql`
  mutation saveAsignacionComanderas($input: AsignacionComanderasInput!) {
    saveAsignacionComanderas(input: $input)
  }
`;

const PRINT_TEST_COMANDERA = gql`
  mutation printTestComandera($id: ID!) {
    printTestComandera(id: $id) {
      success
      message
    }
  }
`;

const gridTemplateColumns = "minmax(220px, 2fr) minmax(180px, 1fr) 56px";
const MENSAJE_APP_FUERA_SERVICIO = "Aplicacion de comanderas fuera de servicio";
const MENSAJE_SIN_DATOS = "No hay datos de comanderas disponibles";
const MENSAJE_PRINT_TEST_ERROR = "La impresion de prueba fallo";

const isAvailableAndEnabled = (row) => {
  const estadoConexion = String(row?.estadoConexion || "").trim().toLowerCase();
  return row?.habilitado === true && estadoConexion === "available";
};

const getEstadoLabel = (row) => {
  if (!row?.habilitado) {
    return { label: "DESHABILITADA", color: "text" };
  }

  if (row?.estadoConexion === "available") {
    return { label: "DISPONIBLE", color: "success" };
  }

  return { label: "OFFLINE", color: "warning" };
};

function ModalComanderas({ open, handleClose }) {
  const { handleSnackbar } = useContext(MessageManager);

  const [tabValue, setTabValue] = useState(0);
  const [comanderas, setComanderas] = useState([]);
  const [mensajeEstado, setMensajeEstado] = useState("");

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const [categoriaSeleccionada, setCategoriaSeleccionada] = useState(null);
  const [idsComanderasFiltro, setIdsComanderasFiltro] = useState([]);
  const [productosBase, setProductosBase] = useState([]);
  const [idsProductosDerecha, setIdsProductosDerecha] = useState([]);
  const [seleccionIzquierda, setSeleccionIzquierda] = useState([]);
  const [seleccionDerecha, setSeleccionDerecha] = useState([]);

  const [fetchComanderas, { loading: loadingComanderas }] = useLazyQuery(
    GET_COMANDERAS,
    {
      fetchPolicy: "network-only",
    }
  );
  const [getCategoriasProducto, { data: dataCategorias, loading: loadingCategorias }] =
    useLazyQuery(GET_CATEGORIAS_PRODUCTO, {
      fetchPolicy: "network-only",
    });
  const [getProductosAsignaciones, { loading: loadingProductosAsignaciones }] =
    useLazyQuery(GET_PRODUCTOS_ASIGNACIONES, {
      fetchPolicy: "network-only",
    });

  const [saveComandera, { loading: loadingSaveComandera }] =
    useMutation(SAVE_COMANDERA);
  const [saveAsignacionComanderas, { loading: loadingGuardarAsignaciones }] =
    useMutation(SAVE_ASIGNACIONES_COMANDERAS);
  const [printTestComandera] = useMutation(PRINT_TEST_COMANDERA);
  const [loadingPrintTest, setLoadingPrintTest] = useState(false);
  const wasOpenRef = useRef(false);

  const loadComanderas = useCallback(async () => {
    try {
      const response = await fetchComanderas({
        variables: { order: { field: "printName", order: "ASC" } },
      });

      if (response?.error || (response?.errors && response.errors.length > 0)) {
        throw response.error || new Error(MENSAJE_APP_FUERA_SERVICIO);
      }

      const rows = response?.data?.comanderas || [];
      setComanderas(rows);

      if (rows.length === 0) {
        setMensajeEstado(MENSAJE_SIN_DATOS);
        handleSnackbar(MENSAJE_SIN_DATOS, "warning");
      } else {
        setMensajeEstado("");
      }
    } catch (error) {
      console.error(error);
      setComanderas([]);
      setMensajeEstado(MENSAJE_APP_FUERA_SERVICIO);
      handleSnackbar(MENSAJE_APP_FUERA_SERVICIO, "error");
    }
  }, [fetchComanderas, handleSnackbar]);

  const loadCategorias = useCallback(async () => {
    try {
      await getCategoriasProducto();
    } catch (error) {
      console.error(error);
      handleSnackbar("No se pudieron cargar las categorias", "warning");
    }
  }, [getCategoriasProducto, handleSnackbar]);

  const loadProductosBase = useCallback(async () => {
    try {
      const response = await getProductosAsignaciones();
      const rows = response?.data?.productos || [];
      setProductosBase(rows);
      return rows;
    } catch (error) {
      console.error(error);
      setProductosBase([]);
      handleSnackbar("No se pudieron cargar los productos", "warning");
      return [];
    }
  }, [getProductosAsignaciones, handleSnackbar]);

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    const justClosed = !open && wasOpenRef.current;

    if (justOpened) {
      loadComanderas();
      loadCategorias();
      loadProductosBase();
    }

    if (justClosed) {
      setTabValue(0);
      setComanderas([]);
      setMensajeEstado("");
      setMenuAnchorEl(null);
      setSelectedRow(null);
      setCategoriaSeleccionada(null);
      setIdsComanderasFiltro([]);
      setProductosBase([]);
      setIdsProductosDerecha([]);
      setSeleccionIzquierda([]);
      setSeleccionDerecha([]);
    }

    wasOpenRef.current = open;
  }, [loadCategorias, loadComanderas, loadProductosBase, open]);

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setSelectedRow(null);
  };

  const handleOpenMenu = (event, row) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const toggleHabilitado = async (row) => {
    if (!row) return;
    try {
      await saveComandera({
        variables: {
          input: {
            id: row.id,
            printName: row.printName,
            estadoConexion: row.estadoConexion,
            habilitado: !row.habilitado,
          },
        },
      });
      handleSnackbar(
        row.habilitado ? "Comandera deshabilitada" : "Comandera habilitada",
        "success"
      );
      closeMenu();
      await loadComanderas();
    } catch (error) {
      console.error(error);
      handleSnackbar("No se pudo actualizar la comandera", "error");
    }
  };

  const handlePrintTest = async (row) => {
    if (!row || !isAvailableAndEnabled(row) || loadingPrintTest) return;

    try {
      setLoadingPrintTest(true);
      const response = await printTestComandera({
        variables: { id: row.id },
      });

      const result = response?.data?.printTestComandera;
      const isSuccess = result?.success === true;
      const message =
        result?.message || (isSuccess ? "Impresion de prueba exitosa" : MENSAJE_PRINT_TEST_ERROR);

      handleSnackbar(`${isSuccess ? "Exito" : "Error"}: ${message}`, isSuccess ? "success" : "error");
    } catch (error) {
      console.error(error);
      const message = error?.message || MENSAJE_PRINT_TEST_ERROR;
      handleSnackbar(`Error: ${message}`, "error");
    } finally {
      setLoadingPrintTest(false);
      closeMenu();
    }
  };

  const productosById = useMemo(() => {
    const map = new Map();
    productosBase.forEach((producto) => {
      map.set(String(producto.id), producto);
    });
    return map;
  }, [productosBase]);

  const selectedCategoryId = categoriaSeleccionada?.id
    ? String(categoriaSeleccionada.id)
    : null;
  const selectedComanderasSet = useMemo(
    () => new Set(idsComanderasFiltro.map(String)),
    [idsComanderasFiltro]
  );

  const baseline = useMemo(() => {
    if (!selectedCategoryId && selectedComanderasSet.size === 0) {
      return { universeIds: [], rightOriginalIds: [] };
    }

    const universeIds = [];
    const rightOriginalIds = [];

    productosBase.forEach((producto) => {
      const idProducto = String(producto.id);
      const idCategoria = String(producto?.categoriaProducto?.id || "");
      const productoComanderas = new Set(
        (producto.comanderas || []).map((comandera) => String(comandera.id))
      );

      const matchCategoria =
        !!selectedCategoryId && idCategoria === String(selectedCategoryId);
      const matchComanderas =
        selectedComanderasSet.size > 0 &&
        [...selectedComanderasSet].every((id) => productoComanderas.has(id));

      if (selectedCategoryId && selectedComanderasSet.size > 0) {
        if (matchCategoria) {
          universeIds.push(idProducto);
        }
        if (matchCategoria && matchComanderas) {
          rightOriginalIds.push(idProducto);
        }
        return;
      }

      if (selectedCategoryId && matchCategoria) {
        universeIds.push(idProducto);
        rightOriginalIds.push(idProducto);
        return;
      }

      if (selectedComanderasSet.size > 0 && matchComanderas) {
        universeIds.push(idProducto);
        rightOriginalIds.push(idProducto);
      }
    });

    return { universeIds, rightOriginalIds };
  }, [productosBase, selectedCategoryId, selectedComanderasSet]);

  useEffect(() => {
    setIdsProductosDerecha(baseline.rightOriginalIds);
    setSeleccionIzquierda([]);
    setSeleccionDerecha([]);
  }, [baseline]);

  const universeSet = useMemo(
    () => new Set(baseline.universeIds.map(String)),
    [baseline.universeIds]
  );
  const originalRightSet = useMemo(
    () => new Set(baseline.rightOriginalIds.map(String)),
    [baseline.rightOriginalIds]
  );
  const currentRightSet = useMemo(
    () => new Set(idsProductosDerecha.map(String)),
    [idsProductosDerecha]
  );

  const movedToRightSet = useMemo(
    () =>
      new Set(
        idsProductosDerecha
          .map(String)
          .filter((id) => !originalRightSet.has(id) && universeSet.has(id))
      ),
    [idsProductosDerecha, originalRightSet, universeSet]
  );

  const movedToLeftSet = useMemo(
    () =>
      new Set(
        baseline.rightOriginalIds
          .map(String)
          .filter((id) => !currentRightSet.has(id) && universeSet.has(id))
      ),
    [baseline.rightOriginalIds, currentRightSet, universeSet]
  );

  const universeProductos = useMemo(
    () =>
      baseline.universeIds
        .map((id) => productosById.get(String(id)))
        .filter(Boolean),
    [baseline.universeIds, productosById]
  );

  const productosDerecha = useMemo(
    () =>
      universeProductos.filter((producto) =>
        currentRightSet.has(String(producto.id))
      ),
    [currentRightSet, universeProductos]
  );

  const productosIzquierda = useMemo(
    () =>
      universeProductos.filter(
        (producto) => !currentRightSet.has(String(producto.id))
      ),
    [currentRightSet, universeProductos]
  );

  const toggleSeleccionTransfer = (lado, idProducto) => {
    const id = String(idProducto);
    if (lado === "izquierda") {
      setSeleccionIzquierda((prev) =>
        prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
      );
      return;
    }

    setSeleccionDerecha((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
    );
  };

  const moverSeleccionadosADerecha = () => {
    if (seleccionIzquierda.length === 0) return;
    setIdsProductosDerecha((prev) => [...new Set([...prev, ...seleccionIzquierda])]);
    setSeleccionIzquierda([]);
  };

  const moverTodosADerecha = () => {
    setIdsProductosDerecha(baseline.universeIds.map(String));
    setSeleccionIzquierda([]);
  };

  const moverSeleccionadosAIzquierda = () => {
    if (seleccionDerecha.length === 0) return;
    const setMover = new Set(seleccionDerecha.map(String));
    setIdsProductosDerecha((prev) =>
      prev.filter((id) => !setMover.has(String(id)))
    );
    setSeleccionDerecha([]);
  };

  const moverTodosAIzquierda = () => {
    setIdsProductosDerecha([]);
    setSeleccionDerecha([]);
  };

  const productoContieneComanderasSeleccionadas = useCallback(
    (producto) => {
      if (!producto || selectedComanderasSet.size === 0) return false;
      const productoComanderas = new Set(
        (producto.comanderas || []).map((comandera) => String(comandera.id))
      );
      return [...selectedComanderasSet].every((id) => productoComanderas.has(id));
    },
    [selectedComanderasSet]
  );

  const handleGuardarAsignaciones = async () => {
    if (idsComanderasFiltro.length === 0) {
      handleSnackbar("Debe seleccionar al menos una comandera", "warning");
      return;
    }

    const movedToRight = [...movedToRightSet];
    const movedToLeft = [...movedToLeftSet];

    if (movedToRight.length === 0 && movedToLeft.length === 0) {
      handleSnackbar("No hay cambios para guardar", "info");
      return;
    }

    const idsProductosAgregar = movedToRight
      .filter((id) => {
        const producto = productosById.get(String(id));
        return !productoContieneComanderasSeleccionadas(producto);
      })
      .map((id) => Number(id));

    const idsProductosQuitar = movedToLeft
      .filter((id) => {
        const producto = productosById.get(String(id));
        return productoContieneComanderasSeleccionadas(producto);
      })
      .map((id) => Number(id));

    if (idsProductosAgregar.length === 0 && idsProductosQuitar.length === 0) {
      handleSnackbar("No hay cambios efectivos para guardar", "info");
      return;
    }

    try {
      await saveAsignacionComanderas({
        variables: {
          input: {
            idsComanderas: idsComanderasFiltro.map((id) => Number(id)),
            idsProductosAgregar,
            idsProductosQuitar,
          },
        },
      });

      handleSnackbar("Asignaciones guardadas correctamente", "success");
      await loadProductosBase();
    } catch (error) {
      console.error(error);
      handleSnackbar("No se pudieron guardar las asignaciones", "error");
    }
  };

  const renderRow = (row) => {
    const estado = getEstadoLabel(row);

    return (
      <Box
        key={`row-${row.id}`}
        sx={{
          display: "grid",
          gridTemplateColumns,
          alignItems: "center",
          minHeight: 56,
          borderTop: "1px solid #e0e0e0",
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>{row.printName}</Box>
        <Box sx={{ px: 2, py: 1 }}>
          <SoftTypography variant="button" color={estado.color} fontWeight="bold">
            {estado.label}
          </SoftTypography>
        </Box>
        <Box sx={{ px: 1, py: 1, display: "flex", justifyContent: "flex-end" }}>
          <IconButton size="small" onClick={(event) => handleOpenMenu(event, row)}>
            <Icon>more_vert</Icon>
          </IconButton>
        </Box>
      </Box>
    );
  };

  const renderTransferList = (titulo, productos, lado, seleccionados) => (
    <Box
      sx={{
        border: "1px solid #d0d4da",
        borderRadius: "0.75rem",
        overflow: "hidden",
        minHeight: 320,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ borderBottom: "1px solid #d0d4da", p: 1.25, textAlign: "center" }}>
        <SoftTypography variant="button" fontWeight="bold">
          {titulo}
        </SoftTypography>
      </Box>
      <Box sx={{ overflowY: "auto", p: 1, flex: 1 }}>
        {productos.length === 0 ? (
          <SoftTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
            Sin productos
          </SoftTypography>
        ) : (
          productos.map((producto) => {
            const idProducto = String(producto.id);
            const checked = seleccionados.includes(idProducto);
            const movedToRight = movedToRightSet.has(idProducto);
            const movedToLeft = movedToLeftSet.has(idProducto);

            const backgroundDiff = movedToRight
              ? "rgba(76, 175, 80, 0.18)"
              : movedToLeft
                ? "rgba(255, 152, 0, 0.2)"
                : "transparent";

            return (
              <Box
                key={`${lado}-${idProducto}`}
                onClick={() => toggleSeleccionTransfer(lado, idProducto)}
                sx={{
                  cursor: "pointer",
                  borderRadius: "0.5rem",
                  px: 1,
                  py: 0.5,
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: checked
                    ? "rgba(25, 118, 210, 0.12)"
                    : backgroundDiff,
                  borderLeft: movedToRight
                    ? "4px solid rgba(76, 175, 80, 0.85)"
                    : movedToLeft
                      ? "4px solid rgba(255, 152, 0, 0.9)"
                      : "4px solid transparent",
                  "&:hover": {
                    backgroundColor: checked
                      ? "rgba(25, 118, 210, 0.18)"
                      : "rgba(25, 118, 210, 0.05)",
                  },
                }}
              >
                <Checkbox checked={checked} size="small" />
                <SoftTypography variant="button">{producto.nombre}</SoftTypography>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );

  return (
    <Modal open={open} onClose={handleClose} closeAfterTransition>
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox p={3}>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <SoftTypography variant="h5">Configuracion de comanderas</SoftTypography>
              <SoftButton color="error" circular iconOnly onClick={handleClose}>
                <Icon>close</Icon>
              </SoftButton>
            </SoftBox>

            <Tabs value={tabValue} onChange={(event, value) => setTabValue(value)} sx={{ mb: 2 }}>
              <Tab label="COMANDERAS" />
              <Tab label="ASIGNACIONES" />
            </Tabs>

            {tabValue === 0 && (
              <Box>
                <SoftBox display="flex" justifyContent="flex-end" mb={1}>
                  <SoftButton
                    color="info"
                    circular
                    onClick={loadComanderas}
                    disabled={loadingComanderas}
                  >
                    <Icon sx={{ mr: 1 }}>refresh</Icon>
                    Refrescar
                  </SoftButton>
                </SoftBox>
                <Box
                  sx={{
                    width: "100%",
                    overflowX: "auto",
                    borderRadius: "0.75rem",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <Box sx={{ minWidth: 560 }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns,
                        alignItems: "center",
                        minHeight: 56,
                      }}
                    >
                      <Box sx={{ px: 2 }}>
                        <SoftTypography variant="button" fontWeight="bold">
                          NOMBRE
                        </SoftTypography>
                      </Box>
                      <Box sx={{ px: 2 }}>
                        <SoftTypography variant="button" fontWeight="bold">
                          ESTADO
                        </SoftTypography>
                      </Box>
                      <Box />
                    </Box>

                    {loadingComanderas ? (
                      <Box sx={{ borderTop: "1px solid #e0e0e0", p: 2 }}>
                        <Loading />
                      </Box>
                    ) : comanderas.length === 0 ? (
                      <Box sx={{ borderTop: "1px solid #e0e0e0", p: 2 }}>
                        <SoftTypography variant="button" color="text">
                          {mensajeEstado || MENSAJE_SIN_DATOS}
                        </SoftTypography>
                      </Box>
                    ) : (
                      comanderas.map((row) => renderRow(row))
                    )}
                  </Box>
                </Box>
              </Box>
            )}

            {tabValue === 1 && (
              <Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Box>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Categoria de productos
                    </SoftTypography>
                    <Autocomplete
                      options={dataCategorias?.categoriasProducto || []}
                      loading={loadingCategorias}
                      value={categoriaSeleccionada}
                      onChange={(_, value) => {
                        setCategoriaSeleccionada(value || null);
                      }}
                      getOptionLabel={(option) => option?.categoria || ""}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Seleccione categoria"
                          size="small"
                        />
                      )}
                    />
                  </Box>

                  <Box>
                    <SoftTypography variant="caption" fontWeight="bold">
                      Comanderas
                    </SoftTypography>
                    <Select
                      multiple
                      fullWidth
                      size="small"
                      value={idsComanderasFiltro}
                      onChange={(event) => {
                        const value = event.target.value;
                        setIdsComanderasFiltro(
                          typeof value === "string" ? value.split(",") : value
                        );
                      }}
                      renderValue={(selected) => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {selected.map((id) => {
                            const comandera = comanderas.find(
                              (item) => String(item.id) === String(id)
                            );
                            return (
                              <Chip
                                key={`chip-comandera-${id}`}
                                label={comandera?.printName || id}
                                size="small"
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {comanderas.map((comandera) => (
                        <MenuItem key={comandera.id} value={String(comandera.id)}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Checkbox
                              checked={idsComanderasFiltro.includes(String(comandera.id))}
                              sx={{ mr: 1, p: 0 }}
                            />
                            {comandera.printName}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Box>

                {(loadingProductosAsignaciones || loadingCategorias) && (
                  <Box sx={{ mb: 2 }}>
                    <Loading />
                  </Box>
                )}

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr auto 1fr" },
                    gap: 2,
                    alignItems: "stretch",
                  }}
                >
                  {renderTransferList(
                    "Sin filtro final",
                    productosIzquierda,
                    "izquierda",
                    seleccionIzquierda
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "row", md: "column" },
                      gap: 1,
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <SoftButton
                      color="info"
                      circular
                      iconOnly
                      onClick={moverSeleccionadosADerecha}
                      disabled={seleccionIzquierda.length === 0}
                    >
                      <Icon>chevron_right</Icon>
                    </SoftButton>
                    <SoftButton
                      color="info"
                      circular
                      iconOnly
                      onClick={moverTodosADerecha}
                      disabled={productosIzquierda.length === 0}
                    >
                      <Icon>keyboard_double_arrow_right</Icon>
                    </SoftButton>
                    <SoftButton
                      color="info"
                      circular
                      iconOnly
                      onClick={moverSeleccionadosAIzquierda}
                      disabled={seleccionDerecha.length === 0}
                    >
                      <Icon>chevron_left</Icon>
                    </SoftButton>
                    <SoftButton
                      color="info"
                      circular
                      iconOnly
                      onClick={moverTodosAIzquierda}
                      disabled={productosDerecha.length === 0}
                    >
                      <Icon>keyboard_double_arrow_left</Icon>
                    </SoftButton>
                  </Box>

                  {renderTransferList(
                    "Filtro actual",
                    productosDerecha,
                    "derecha",
                    seleccionDerecha
                  )}
                </Box>

                <SoftBox display="flex" justifyContent="flex-end" mt={2}>
                  <SoftButton
                    color="primary"
                    circular
                    onClick={handleGuardarAsignaciones}
                    disabled={
                      idsComanderasFiltro.length === 0 ||
                      loadingGuardarAsignaciones ||
                      loadingProductosAsignaciones
                    }
                  >
                    <Icon sx={{ mr: 1 }}>save</Icon>
                    Guardar
                  </SoftButton>
                </SoftBox>
              </Box>
            )}
          </SoftBox>

          <Menu
            anchorEl={menuAnchorEl}
            open={Boolean(menuAnchorEl)}
            onClose={closeMenu}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <MenuItem
              onClick={() => toggleHabilitado(selectedRow)}
              disabled={loadingSaveComandera}
            >
              {selectedRow?.habilitado ? "Deshabilitar" : "Habilitar"}
            </MenuItem>
            {isAvailableAndEnabled(selectedRow) && (
              <MenuItem
                onClick={() => handlePrintTest(selectedRow)}
                disabled={loadingPrintTest}
              >
                Print test
              </MenuItem>
            )}
          </Menu>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalComanderas.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default ModalComanderas;

